import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrip } from '@/context/TripContext';
import { routeTemplates } from '@/mocks/routeData';
import { places } from '@/mocks/placesData';

const daysOptions = [
  { label: '一日游', desc: '当天往返' },
  { label: '两天一夜', desc: '周末短游' },
  { label: '三天两夜', desc: '小长假', default: true },
  { label: '4-5天', desc: '深度游' },
];

const crowdOptions = ['亲子', '情侣', '朋友', '老人', '团建'];

const prefOptions = [
  { label: '轻松', icon: 'ri-walk-line' },
  { label: '拍照', icon: 'ri-camera-line' },
  { label: '户外', icon: 'ri-tent-line' },
  { label: '咖啡', icon: 'ri-cup-line' },
  { label: '避暑', icon: 'ri-sun-line' },
  { label: '雨天', icon: 'ri-rainy-line' },
  { label: '人少', icon: 'ri-user-line' },
];

export default function CreateRoute() {
  const navigate = useNavigate();
  const { selectedSpots, removeSpot, startRoute } = useTrip();

  const [step, setStep] = useState(1);
  const [selectedDay, setSelectedDay] = useState('三天两夜');
  const [selectedCrowd, setSelectedCrowd] = useState('亲子');
  const [selectedPrefs, setSelectedPrefs] = useState<string[]>(['拍照']);

  const togglePref = (pref: string) => {
    setSelectedPrefs((prev) =>
      prev.includes(pref) ? prev.filter((p) => p !== pref) : [...prev, pref],
    );
  };

  const stepTitles = ['选择天数', '选择人群', '选择偏好', '确认地点'];

  const handleGenerate = () => {
    const config = {
      days: selectedDay,
      crowd: selectedCrowd,
      preferences: selectedPrefs,
    };

    // 根据选择的天数匹配对应路线模板
    const route = routeTemplates[selectedDay];
    if (route) {
      startRoute(route, config);
    }

    navigate('/route-result');
  };

  // 从 placesData 查找地点名称
  const getPlaceName = (spotId: string) => {
    const place = places.find((p) => p.id === spotId);
    return place?.name || spotId;
  };

  return (
    <div className="min-h-screen bg-bg pb-28">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-bg px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <button
            className="w-8 h-8 flex items-center justify-center cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <i className="ri-arrow-left-line text-lg text-text-body" />
          </button>
          <h1 className="text-lg font-bold text-text-primary">生成路线</h1>
        </div>
        <p className="text-sm text-text-muted mt-1">选择天数和偏好，生成你的安吉路线</p>
      </header>

      {/* Step indicator */}
      <div className="px-4 mt-3">
        <div className="flex items-center justify-between">
          {stepTitles.map((title, i) => (
            <div key={title} className="flex flex-col items-center flex-1">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  step > i + 1
                    ? 'bg-bamboo text-white'
                    : step === i + 1
                    ? 'bg-bamboo text-white'
                    : 'bg-white text-text-muted border border-divider'
                }`}
              >
                {step > i + 1 ? <i className="ri-check-line" /> : i + 1}
              </div>
              <span
                className={`text-[10px] mt-1 font-medium ${
                  step === i + 1 ? 'text-bamboo' : 'text-text-muted'
                }`}
              >
                {title}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-3 h-px bg-divider" />
      </div>

      {/* Step 1: Days */}
      {step === 1 && (
        <div className="px-4 mt-5">
          <h2 className="text-base font-bold text-text-primary">你打算玩几天？</h2>
          <div className="grid grid-cols-2 gap-3 mt-3">
            {daysOptions.map((opt) => (
              <button
                key={opt.label}
                onClick={() => setSelectedDay(opt.label)}
                className={`p-4 rounded-xl text-left cursor-pointer transition-colors border ${
                  selectedDay === opt.label
                    ? 'bg-bamboo-pale border-bamboo text-bamboo'
                    : 'bg-white border-divider text-text-body'
                }`}
              >
                <span className="text-base font-bold block">{opt.label}</span>
                <span
                  className={`text-xs mt-1 block ${
                    selectedDay === opt.label ? 'text-bamboo/80' : 'text-text-muted'
                  }`}
                >
                  {opt.desc}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Crowd */}
      {step === 2 && (
        <div className="px-4 mt-5">
          <h2 className="text-base font-bold text-text-primary">和谁一起？</h2>
          <div className="grid grid-cols-3 gap-3 mt-3">
            {crowdOptions.map((opt) => (
              <button
                key={opt}
                onClick={() => setSelectedCrowd(opt)}
                className={`py-3 px-2 rounded-xl text-sm font-medium cursor-pointer transition-colors border ${
                  selectedCrowd === opt
                    ? 'bg-bamboo-pale border-bamboo text-bamboo'
                    : 'bg-white border-divider text-text-body'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 3: Preferences */}
      {step === 3 && (
        <div className="px-4 mt-5">
          <h2 className="text-base font-bold text-text-primary">偏好什么玩法？</h2>
          <p className="text-xs text-text-muted mt-1">可多选</p>
          <div className="grid grid-cols-3 gap-3 mt-3">
            {prefOptions.map((opt) => {
              const active = selectedPrefs.includes(opt.label);
              return (
                <button
                  key={opt.label}
                  onClick={() => togglePref(opt.label)}
                  className={`py-3 rounded-xl text-sm font-medium cursor-pointer transition-colors border flex flex-col items-center gap-1 ${
                    active
                      ? 'bg-bamboo-pale border-bamboo text-bamboo'
                      : 'bg-white border-divider text-text-body'
                  }`}
                >
                  <i className={`${opt.icon} text-lg`} />
                  <span>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 4: Confirm spots */}
      {step === 4 && (
        <div className="px-4 mt-5">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-text-primary">已选地点</h2>
            <button
              className="text-sm text-bamboo cursor-pointer"
              onClick={() => navigate('/explore')}
            >
              + 添加更多
            </button>
          </div>

          <div className="mt-3 space-y-2">
            {selectedSpots.map((spotId) => (
              <div
                key={spotId}
                className="flex items-center justify-between bg-white rounded-xl px-3 py-2.5"
              >
                <span className="text-sm font-medium text-text-body">
                  {getPlaceName(spotId)}
                </span>
                <button
                  className="w-7 h-7 flex items-center justify-center cursor-pointer text-text-muted hover:text-red-500"
                  onClick={() => removeSpot(spotId)}
                >
                  <i className="ri-close-line" />
                </button>
              </div>
            ))}
            {selectedSpots.length === 0 && (
              <div className="text-center py-6 text-text-muted text-sm">
                还没有选择地点
                <br />
                <button
                  className="text-bamboo mt-2 cursor-pointer"
                  onClick={() => navigate('/explore')}
                >
                  去添加地点
                </button>
              </div>
            )}
          </div>

          <div className="mt-4 bg-bamboo-pale rounded-xl p-3">
            <p className="text-xs text-bamboo flex items-center gap-1.5">
              <i className="ri-information-line" />
              已优先参考你加入的地点，系统会自动补充顺路点位
            </p>
          </div>

          {selectedSpots.length === 0 && (
            <div className="mt-2 bg-stream/10 rounded-xl p-3">
              <p className="text-xs text-stream flex items-center gap-1.5">
                <i className="ri-lightbulb-line" />
                未选择地点也可以生成推荐路线
              </p>
            </div>
          )}

          <div className="mt-2 bg-stream/10 rounded-xl p-3">
            <p className="text-xs text-stream flex items-center gap-1.5">
              <i className="ri-route-line" />
              路线类型：{selectedDay} · {selectedCrowd} · {selectedPrefs.join('、')}
            </p>
          </div>
        </div>
      )}

      {/* Bottom buttons */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-divider px-4 py-3">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          {step > 1 && (
            <button
              className="px-5 py-2.5 rounded-lg border border-divider text-sm font-medium text-text-body cursor-pointer bg-white"
              onClick={() => setStep(step - 1)}
            >
              上一步
            </button>
          )}
          {step < 4 ? (
            <button
              className="flex-1 py-2.5 rounded-lg text-sm font-medium text-white bg-bamboo cursor-pointer"
              onClick={() => setStep(step + 1)}
            >
              下一步
            </button>
          ) : (
            <button
              className="flex-1 py-2.5 rounded-lg text-sm font-medium text-white bg-bamboo cursor-pointer"
              onClick={handleGenerate}
            >
              生成我的安吉路线
            </button>
          )}
        </div>
      </div>
    </div>
  );
}